import type { ExperienceBlockDraft, ExperienceVariable } from '@matter_extractor/shared';
import { renderRichText } from '@matter_extractor/shared';
import type { ReactNode } from 'react';
import { readerPreviewHtml } from '../reader-text';

export interface TextBlockProps {
  readonly block: Extract<ExperienceBlockDraft, { type: 'text' }>;
  readonly resolveVariable: ((variable: ExperienceVariable) => string | null) | undefined;
  /** A preview NAMES its variables rather than resolving them (D4, D5) - see `reader-text.ts`. */
  readonly variableNames: ((variable: ExperienceVariable) => string) | undefined;
}

/**
 * The Texte block (§9.2): the closed tree, rendered by the ONE pure function
 * `shared` exports for it.
 *
 * §9.2 puts a single condition on the storage format - « le rendu est une
 * fonction pure, jouée à l'identique par l'aperçu de l'éditeur et par le
 * lecteur » - so this component computes nothing: it hands the tree to
 * `renderRichText` and paints the answer. Marks come back as `<strong>`,
 * `<em>` and `<u>`, a coloured passage as `data-color`, which `reader.css`
 * paints from the theme's own three colours (H28).
 *
 * A preview - the editor's, the Timeline's - hands `variableNames` and gets the
 * same markup with each token drawn as a pill in the theme's accent, carrying
 * the variable's NAME (D5): `readerPreviewHtml` goes through the very same
 * renderer, so the preview and the session cannot paint two texts.
 *
 * A preview that hands BOTH - the full-screen preview of #141, and nothing else
 * - stays on that path and gives it its resolver: a token the resolver answers
 * comes out as its value, the rest keep the pill. The SESSION is told apart by
 * `variableNames` being absent, never by the resolver being present, so its
 * markup is the one it has always had.
 *
 * **`dangerouslySetInnerHTML` is used here, and in `noyau-block.tsx` for the
 * texts of the noyaux, which are this very format (noyau.md §6.2).** `renderRichText`
 * takes a TREE of known nodes and escapes every character of text it emits -
 * `&`, `<`, `>`, `"` and `'` - so there is no path by which an operator's
 * keystroke becomes an element; the pill's label is escaped the same way.
 * The alternative, walking the tree in JSX, would be a second renderer, which
 * is the very thing §9.2 forbids.
 */
export function TextBlock({ block, resolveVariable, variableNames }: TextBlockProps): ReactNode {
  const html =
    variableNames === undefined
      ? renderRichText(block.text, resolveVariable === undefined ? {} : { resolveVariable })
      : readerPreviewHtml(block.text, variableNames, resolveVariable);
  return (
    <div
      className="lecteur-texte"
      // biome-ignore lint/security/noDangerouslySetInnerHtml: escaped by construction - see the note above.
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
